/**
 * The Pick'em round strip (ISSUE-1304): one chip per round, scrolling
 * sideways, with the selected chip kept in view. Each chip says the round,
 * its dates and the fan's own progress, all from `usePickemPoolGet`: picks
 * made of picks needed while the round is open, points once it is scored.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef } from "react";

import {
  PICKEM_ROUNDS_LABEL,
  pickemPointsLabel,
  pickemRoundChipLabel,
} from "./pickemCopy";
import type { typePickemPoolRound } from "./pickemTypes";

export function pickemRoundChipStatus(round: typePickemPoolRound): string {
  if (round.status === "graded" && typeof round.myPoints === "number") {
    return pickemPointsLabel(round.myPoints);
  }
  if (round.myPicksRequired > 0) {
    return `${round.myPicksMade}/${round.myPicksRequired}`;
  }
  return "";
}

export const PickemRoundStrip = (props: {
  rounds: readonly typePickemPoolRound[];
  selectedRoundId: string;
  onSelectRound: (roundId: string) => void;
}): ReactNode => {
  const listRef = useRef<HTMLDivElement>(null);
  const roundKey = props.rounds.map((round) => round.roundId).join(",");

  useEffect(() => {
    const container = listRef.current;
    if (container === null) return;
    const target = [
      ...container.querySelectorAll<HTMLElement>("[data-round-id]"),
    ].find((element) => element.dataset.roundId === props.selectedRoundId);
    target?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [props.selectedRoundId, roundKey]);

  if (props.rounds.length === 0) return null;

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={PICKEM_ROUNDS_LABEL}
      data-testid="pickem-round-strip"
      className="flex gap-2 overflow-x-auto [scrollbar-width:none]"
    >
      {props.rounds.map((round) => {
        const isSelected = round.roundId === props.selectedRoundId;
        const status = pickemRoundChipStatus(round);
        return (
          <button
            key={round.roundId}
            type="button"
            role="tab"
            aria-selected={isSelected}
            data-round-id={round.roundId}
            data-testid={`pickem-round-chip-${round.roundId}`}
            className={cn(
              "flex min-h-touch-target min-w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-md px-3 py-2",
              "transition-colors duration-200 motion-reduce:transition-none",
              isSelected
                ? "border-2 border-primary bg-surface"
                : "border border-hairline bg-surface",
              FOCUS_RING,
            )}
            onClick={() => {
              props.onSelectRound(round.roundId);
            }}
          >
            <span className="text-small font-semibold text-ink-secondary">
              {pickemRoundChipLabel(round.ordinal)}
            </span>
            <span className="text-caption text-ink">{round.label}</span>
            {status === "" ? null : (
              <span className="text-caption text-ink-secondary tabular-nums">
                {status}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
